import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api, apiError, ROLE_LABELS, MANAGEMENT_ROLES } from "../../lib/api";
import { toast } from "sonner";
import { Emblem } from "../../components/PublicLayout";
import DataManager from "./adminData";
import { Dashboard, Quotes, Bookings, Invoices, Discounts, DiamondAI, Reports, Audit, InsurancePanel, Calendar, LiveOps, Payroll, FamilyDiscount, OwnerBenefit, SpecialistPricing } from "./adminViews";
import {
  LayoutDashboard, FileText, Calendar as Calendar_, Receipt, Users2, UserCog, Percent, Sparkles, Settings2,
  BarChart3, ShieldCheck, Building2, Megaphone, ClipboardList, Boxes, KeyRound, FolderLock,
  AlertOctagon, Contact2, Briefcase, ListChecks, Share2, ScrollText, LogOut, Menu, X, MapPin, Package, MessageSquare, Bell, Repeat, Wallet
} from "lucide-react";

const GROUPS = [
  ["Operations", [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["live_ops", "Live Operations", Bell],
    ["quotes", "Quotes & Enquiries", FileText],
    ["bookings", "Bookings", Calendar_],
    ["calendar", "Calendar", Calendar_],
    ["invoices", "Invoices & Payments", Receipt],
    ["discounts", "Discount Approvals", Percent],
  ]],
  ["Customers & Sales", [
    ["contacts", "CRM Contacts", Contact2],
    ["messages", "Messages", MessageSquare],
    ["commercial_enquiries", "Commercial Leads", Building2],
    ["commercial_clients", "Commercial Clients", Building2],
    ["commercial_sites", "Commercial Sites", MapPin],
    ["campaigns", "Marketing", Megaphone],
    ["referrals", "Referrals", Share2],
    ["complaints", "Complaints", AlertOctagon],
  ]],
  ["Team", [
    ["staff", "Staff & Cleaners", Users2],
    ["payroll", "Payroll", Receipt],
    ["applicants", "Recruitment", Briefcase],
    ["tasks", "Tasks", ListChecks],
    ["checklist_templates", "Checklists", ClipboardList],
  ]],
  ["Business", [
    ["services", "Services", Package],
    ["packages", "Pricing", Receipt],
    ["specialist_pricing", "Specialist Pricing", Sparkles],
    ["subscription_plans", "Subscription Plans", Repeat],
    ["customer_subscriptions", "Subscriptions", Repeat],
    ["credit_ledger", "Cleaning Credits", Wallet],
    ["service_areas", "Service Areas", MapPin],
    ["inventory", "Inventory", Boxes],
    ["supply_requests", "Supply Requests", Boxes],
    ["compliance_items", "Compliance", ShieldCheck],
    ["documents", "Documents", FolderLock],
    ["key_records", "Key-Holding", KeyRound],
  ]],
  ["Intelligence", [
    ["reports", "Reports", BarChart3],
    ["diamond", "Diamond AI", Sparkles],
    ["settings", "Settings", Settings2],
    ["audit", "Audit Log", ScrollText],
    ["users", "Users & Roles", UserCog],
  ]],
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [active, setActive] = useState("dashboard");
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({});
  const isManagement = MANAGEMENT_ROLES.includes(user?.role);
  const isOwner = user?.role === "founder_ceo";

  const OWNER_GROUP = ["Owner (Private)", [
    ["family_discount", "Family Discount", Users2],
    ["owner_benefit", "Owner Elite Signature", Sparkles],
  ]];
  const NAV_GROUPS = isOwner ? [...GROUPS, OWNER_GROUP] : GROUPS;

  const loadCounts = () => api.get("/enquiries/counts").then(({ data }) => setCounts(data)).catch(() => {});
  useEffect(() => { loadCounts(); const t = setInterval(loadCounts, 30000); return () => clearInterval(t); }, [active]);

  const BADGE_KEY = { quotes: "new_enquiries", messages: "unhandled_messages", commercial_enquiries: "new_commercial" };

  const render = () => {
    switch (active) {
      case "dashboard": return <Dashboard />;
      case "live_ops": return <LiveOps />;
      case "payroll": return <Payroll isManagement={isManagement} />;
      case "specialist_pricing": return <SpecialistPricing isManagement={isManagement} />;
      case "family_discount": return isOwner ? <FamilyDiscount /> : <Dashboard />;
      case "owner_benefit": return isOwner ? <OwnerBenefit /> : <Dashboard />;
      case "quotes": return <Quotes />;
      case "bookings": return <Bookings />;
      case "calendar": return <Calendar />;
      case "invoices": return <Invoices />;
      case "discounts": return <Discounts isManagement={isManagement} />;
      case "reports": return <Reports isManagement={isManagement} />;
      case "diamond": return <DiamondAI />;
      case "settings": return <SettingsView />;
      case "audit": return <Audit />;
      case "users": return <UsersView isFounder={user?.role === "founder_ceo"} />;
      case "compliance_items": return <div><InsurancePanel /><DataManager schemaKey="compliance_items" /></div>;
      default: return <DataManager schemaKey={active} />;
    }
  };

  const NavItem = ([key, label, Icon]) => {
    const badge = counts[BADGE_KEY[key]] || 0;
    return (
      <button key={key} data-testid={`nav-${key}`} onClick={() => { setActive(key); setOpen(false); }}
        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm rounded-sm transition-colors ${active === key ? "bg-[#d4af37] text-[#0a0a0a] font-semibold" : "text-[#cfcabf] hover:bg-[#1c1c1c]"}`}>
        <Icon size={17} /> <span className="flex-1 text-left">{label}</span>
        {badge > 0 && <span data-testid={`badge-${key}`} className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center ${active === key ? "bg-[#0a0a0a] text-[#d4af37]" : "bg-[#d4af37] text-[#0a0a0a]"}`}>{badge}</span>}
      </button>
    );
  };

  const Sidebar = (
    <div className="h-full flex flex-col">
      <div className="px-5 py-4 border-b border-[#333] flex items-center justify-between">
        <Emblem light />
        <button onClick={() => { setActive("quotes"); setOpen(false); }} data-testid="nav-bell" className="relative text-[#cfcabf] hover:text-[#d4af37]" title="New enquiries requiring attention">
          <Bell size={20} />
          {(counts.total || 0) > 0 && <span data-testid="badge-total" className="absolute -top-1.5 -right-1.5 bg-[#d4af37] text-[#0a0a0a] text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center">{counts.total}</span>}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {NAV_GROUPS.map(([group, items]) => (
          <div key={group}>
            <p className="text-[10px] tracking-luxe uppercase text-[#666] px-4 mb-1">{group}</p>
            <div className="space-y-0.5">{items.map(NavItem)}</div>
          </div>
        ))}
      </div>
      <div className="p-3 border-t border-[#333]">
        <p className="px-4 text-xs text-[#a3a3a3]">{user?.name}</p>
        <p className="px-4 text-[10px] text-[#d4af37] mb-2">{ROLE_LABELS[user?.role]}</p>
        <button data-testid="admin-logout" onClick={() => { logout(); nav("/"); }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-[#cfcabf] hover:text-[#d4af37]"><LogOut size={16} /> Sign out</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#faf9f5] flex">
      <aside className="hidden lg:block w-64 bg-[#0a0a0a] fixed inset-y-0 left-0 z-30">{Sidebar}</aside>
      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-50 lg:hidden ${open ? "" : "pointer-events-none"}`}>
        <div className={`absolute inset-0 bg-black/60 transition-opacity ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} />
        <aside className={`absolute inset-y-0 left-0 w-72 bg-[#0a0a0a] transition-transform ${open ? "translate-x-0" : "-translate-x-full"}`}>{Sidebar}</aside>
      </div>

      <div className="flex-1 lg:ml-64">
        <header className="lg:hidden sticky top-0 z-20 bg-[#0a0a0a] h-14 flex items-center justify-between px-4">
          <button data-testid="admin-menu-btn" onClick={() => setOpen(true)} className="text-[#fdfbf7]"><Menu size={24} /></button>
          <Emblem light />
          <span className="w-6" />
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">{render()}</main>
      </div>
    </div>
  );
}

// ---------------- Settings ----------------
const SETTING_KEYS = [
  ["business", "Business"], ["brand", "Brand & Content"], ["booking_rules", "Booking Rules"],
  ["pay_settings", "Pay & Profit"], ["credits", "Credits"],
  ["policies", "Legal Policies"], ["integrations", "Integrations"], ["seo", "SEO"],
];

function SettingsView() {
  const [key, setKey] = useState("business");
  const [value, setValue] = useState(null);
  const load = () => api.get(`/settings/${key}`).then(({ data }) => setValue(data.value)).catch(() => {});
  useEffect(() => { setValue(null); load(); }, [key]);
  const save = async () => {
    try { await api.put(`/settings/${key}`, { value }); toast.success("Settings saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const set = (k, v) => setValue((p) => ({ ...p, [k]: v }));
  const cls = "w-full bg-white border border-[#e0ddd4] rounded-sm px-3 py-2.5 text-sm focus:border-[#d4af37]";

  return (
    <div>
      <h2 className="font-display text-3xl tracking-tight mb-5">Settings</h2>
      <div className="flex gap-2 flex-wrap mb-6">
        {SETTING_KEYS.map(([k, l]) => (
          <button key={k} data-testid={`settings-tab-${k}`} onClick={() => setKey(k)} className={`px-4 py-2 rounded-sm text-sm ${key === k ? "btn-gold" : "btn-outline-gold"}`}>{l}</button>
        ))}
      </div>
      {!value ? <p className="text-[#8a8a8a]">Loading…</p> : (
        <div className="bg-white border border-[#e8e5de] rounded-sm p-6 max-w-2xl space-y-4">
          {key === "integrations" && <p className="text-xs text-[#8a8a8a]">Set each integration's status. Set Email to "Connected" to enable live alert emails.</p>}
          {Object.entries(value).map(([k, v]) => (
            <div key={k}>
              <label className="text-xs tracking-wide text-[#555] uppercase mb-1 block">{k.replace(/_/g, " ")}</label>
              {typeof v === "boolean" ? (
                <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={v} onChange={(e) => set(k, e.target.checked)} className="w-5 h-5 accent-[#d4af37]" data-testid={`set-${k}`} /> Enabled</label>
              ) : typeof v === "number" ? (
                <input type="number" className={cls} value={v} onChange={(e) => set(k, Number(e.target.value))} data-testid={`set-${k}`} />
              ) : Array.isArray(v) ? (
                <input className={cls} value={v.join(", ")} onChange={(e) => set(k, e.target.value.split(",").map((x) => x.trim()))} data-testid={`set-${k}`} />
              ) : (v && v.length > 60) || ["about", "hero_sub", "privacy", "terms", "cancellation", "cookie", "complaints", "accessibility", "dbs_policy", "description"].includes(k) ? (
                <textarea rows="3" className={cls} value={v} onChange={(e) => set(k, e.target.value)} data-testid={`set-${k}`} />
              ) : (
                <input className={cls} value={v} onChange={(e) => set(k, e.target.value)} data-testid={`set-${k}`} />
              )}
            </div>
          ))}
          <button onClick={save} data-testid="settings-save" className="btn-gold px-6 py-2.5 rounded-sm text-sm">Save changes</button>
        </div>
      )}
    </div>
  );
}

// ---------------- Users & Roles ----------------
function UsersView({ isFounder }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/users").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setRole = async (id, role) => { try { await api.put(`/users/${id}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div>
      <h2 className="font-display text-3xl tracking-tight mb-5">Users & Roles</h2>
      {!isFounder && <p className="text-xs text-[#8a8a8a] mb-3">Only the Founder/CEO can change roles.</p>}
      <div className="bg-white border border-[#e8e5de] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#8a8a8a] text-xs border-b border-[#e8e5de]">{["Name", "Email", "Role"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>{rows.map((u) => (
            <tr key={u.id} className="border-b border-[#f0efe9]"><td className="px-4 py-3">{u.name}</td><td className="px-4 py-3">{u.email}</td>
              <td className="px-4 py-3">
                {isFounder ? (
                  <select value={u.role} onChange={(e) => setRole(u.id, e.target.value)} className="border border-[#e0ddd4] rounded-sm px-2 py-1 text-xs" data-testid={`role-${u.id}`}>
                    {Object.keys(ROLE_LABELS).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                  </select>
                ) : ROLE_LABELS[u.role]}
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <StaffInvites />
    </div>
  );
}

function StaffInvites() {
  const [invites, setInvites] = useState([]);
  const [f, setF] = useState({ name: "", email: "", role: "cleaner" });
  const load = () => api.get("/staff/invites").then(({ data }) => setInvites(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async () => {
    if (!f.name || !/.+@.+\..+/.test(f.email)) { toast.error("Enter a name and valid email"); return; }
    try { await api.post("/staff/invites", { ...f, origin_url: window.location.origin }); toast.success(`Invitation emailed to ${f.email}`); setF({ name: "", email: "", role: "cleaner" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const revoke = async (id) => { try { await api.post(`/staff/invites/${id}/revoke`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const ROLES = [["cleaner", "Cleaner"], ["supervisor", "Supervisor"], ["admin", "Administrator"], ["ops_manager", "Operations Manager"]];
  return (
    <div className="mt-8" data-testid="staff-invites">
      <h3 className="font-display text-2xl tracking-tight mb-1">Team invitations</h3>
      <p className="text-xs text-[#8a8a8a] mb-4">Invite a team member by email. They set their own password via a secure one-time link — no shared passwords. Management accounts use two-factor at sign-in.</p>
      <div className="bg-white border border-[#e8e5de] rounded-sm p-5 flex flex-wrap gap-3 items-end">
        <div><label className="text-xs text-[#555] block mb-1">Name</label><input data-testid="invite-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" /></div>
        <div><label className="text-xs text-[#555] block mb-1">Email</label><input data-testid="invite-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" /></div>
        <div><label className="text-xs text-[#555] block mb-1">Role</label>
          <select data-testid="invite-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm">
            {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></div>
        <button data-testid="invite-send" onClick={send} className="btn-gold px-5 py-2 rounded-sm text-sm">Send invite</button>
      </div>
      {invites.length > 0 && (
        <div className="bg-white border border-[#e8e5de] rounded-sm overflow-x-auto mt-4">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#8a8a8a] text-xs border-b border-[#e8e5de]">{["Name", "Email", "Role", "Status", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
            <tbody>{invites.map((i) => (
              <tr key={i.id} className="border-b border-[#f0efe9]" data-testid={`invite-row-${i.id}`}>
                <td className="px-4 py-3">{i.name}</td><td className="px-4 py-3">{i.email}</td>
                <td className="px-4 py-3">{i.role}</td>
                <td className="px-4 py-3"><span className={i.status === "Accepted" ? "text-green-600" : i.status === "Revoked" ? "text-red-500" : "text-[#d4af37]"}>{i.status}</span></td>
                <td className="px-4 py-3">{i.status === "Pending" && <button data-testid={`invite-revoke-${i.id}`} onClick={() => revoke(i.id)} className="text-red-500 text-xs">Revoke</button>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
